/* eslint-disable @typescript-eslint/no-explicit-any */
import { UseFormTools, capitalise } from "@wisr/common";
import { isDebugMode } from "../../../../shared/config.utils";

import { Application, SectionErrors } from "../../../application";
import { ID_TYPES } from "../../../application.utils";
import { ReviewField } from "./review-fields/review-fields";
import { PaymentFrequency } from "../../../application-form.constants";
import { passportCountryList } from "../id-documents/passport/passport.constants";

export const canEditAllFields = (data: ReviewField[]) => {
  if (!data.length || !data[0].editPath) return false;

  const editPath = data[0].editPath;

  return data.every(
    (item) => item.editable === true && item.editPath === editPath
  );
};

export const getIdentityEditPath = (type: string) => {
  switch (type) {
    case ID_TYPES.DRIVERS_LICENCE:
      return "drivers-licence";
    case ID_TYPES.MEDICARE:
      return "medicare";
    case ID_TYPES.PASSPORT:
      return "passport";
    default:
      return "id-documents";
  }
};

interface AppendIdentificationFields {
  baseField: {
    label: string;
    value: string;
    editable: boolean;
    editPath: string | undefined;
  }[];
  formData: UseFormTools<Application>["formData"];
}
export const appendIdentificationFields = ({
  baseField,
  formData,
}: AppendIdentificationFields) => {
  let fields: { label: string; value: string }[] = [];

  switch (baseField[0].value) {
    case ID_TYPES.DRIVERS_LICENCE:
      fields = [
        ...baseField,
        {
          label: "Licence no.",
          value: formData.driverLicenceNumber,
        },
        {
          label: "Card no.",
          value: formData.driverLicenceCardNumber,
        },
        {
          label: "State",
          value: formData.driversLicenceState.toUpperCase(),
        },
        {
          label: "Expiry date",
          value: formData.driversLicenceExpiry,
        },
      ];
      break;
    case ID_TYPES.MEDICARE:
      fields = [
        ...baseField,
        {
          label: "Card colour",
          value: capitalise(formData.medicareColour),
        },
        {
          label: "Card no.",
          value: formData.medicareCardNumber,
        },
        {
          label: "Reference no.",
          value: formData.medicareReferenceNumber,
        },
        {
          label: "Expiry date",
          value: formData.medicareExpiry,
        },
      ];
      break;
    case ID_TYPES.PASSPORT:
      fields = [
        ...baseField,
        {
          label: "Passport no.",
          value: formData.passportNumber,
        },
        {
          label: "Country",
          value:
            passportCountryList.find(
              (country) => country.value === formData.passportCountry
            )?.label ?? capitalise(formData.passportCountry),
        },
      ];
      break;
    default:
      fields = [...baseField];
  }
  return fields;
};

interface GetTimeSpentString {
  years: string;
  months: string;
}
export const getTimeSpentString = ({ years, months }: GetTimeSpentString) => {
  let yearStr = "";
  let monthStr = "";
  if (+years !== 0) {
    if (+years === 1) {
      yearStr = `${years} year`;
    } else {
      yearStr = `${years} years`;
    }
  }

  if (+months !== 0) {
    if (+months === 1) {
      monthStr = `${months} month`;
    } else {
      monthStr = `${months} months`;
    }
  }

  if (yearStr && monthStr) {
    return `${yearStr} & ${monthStr}`;
  }

  if (yearStr) {
    return yearStr;
  }

  if (monthStr) {
    return monthStr;
  }
  return "";
};

export const getPaymentFrequencyString = (frequency: string) => {
  switch (frequency) {
    case PaymentFrequency.ANNUAL:
      return "year";
    case PaymentFrequency.MONTHLY:
      return "month";
    case PaymentFrequency.FORTNIGHTLY:
      return "fortnight";
    case PaymentFrequency.WEEKLY:
      return "week";
  }
};

export const getRentShared = (response?: string) => {
  if (response) {
    return "Yes";
  }

  return "No";
};

export const getHasChanges = (formDataArr: string[]) => {
  const incomeOpts = [
    "long_term_decrease_in_income",
    "short_term_decrease_in_income",
  ];
  const expenseOpts = [
    "long_term_increase_in_expenses",
    "short_term_increase_in_expenses",
  ];
  const combinedOpts = [...incomeOpts, ...expenseOpts];
  let hasChanges = false;
  const labelArr = [];

  for (const data of formDataArr) {
    hasChanges = combinedOpts.some((item) => item === data);

    switch (data) {
      case "long_term_decrease_in_income":
        labelArr.push("Long Term - Decrease in income");
        break;
      case "short_term_decrease_in_income":
        labelArr.push("Short Term - Decrease in income");
        break;
      case "long_term_increase_in_expenses":
        labelArr.push("Long Term - Increase in expenses");
        break;
      case "short_term_increase_in_expenses":
        labelArr.push("Short Term - Increase in expenses");
        break;
    }
  }
  return { hasChanges: hasChanges ? "Yes" : "No", labelArr };
};

export const getTotalSpending = (values: string[]) => {
  const total = values
    .map((value) => (value !== "" ? parseFloat(value) : parseFloat("0")))
    .reduce((partialSum, a) => partialSum + a, 0);

  return total.toFixed(2);
};

interface DebtData {
  type: string;
  company: string;
  balance: string;
  repaymentAmount: string;
  repaymentFrequency: string;
  jointLoan: string[];
  consolidate: string[];
  investment: string[];
  creditCardLimit: string;
  closeCard: string[];
}
export const debtReviewData = (
  formData: UseFormTools<Application>["formData"]
): { [char: number]: DebtData } => {
  return {
    1: {
      type: formData.financialDebtType_1,
      company: formData.financialDebtFinanceCompany_1,
      balance: formData.financialDebtBalance_1,
      repaymentAmount: formData.financialDebtRepaymentAmount_1,
      repaymentFrequency: formData.financialDebtRepaymentFrequency_1,
      jointLoan: formData.financialDebtIsJointLoan_1,
      consolidate: formData.financialDebtConsolidate_1,
      investment: formData.financialDebtIsInvestment_1,
      creditCardLimit: formData.financialDebtCreditCardLimit_1,
      closeCard: formData.financialDebtCloseThisCard_1,
    },
    2: {
      type: formData.financialDebtType_2,
      company: formData.financialDebtFinanceCompany_2,
      balance: formData.financialDebtBalance_2,
      repaymentAmount: formData.financialDebtRepaymentAmount_2,
      repaymentFrequency: formData.financialDebtRepaymentFrequency_2,
      jointLoan: formData.financialDebtIsJointLoan_2,
      consolidate: formData.financialDebtConsolidate_2,
      investment: formData.financialDebtIsInvestment_2,
      creditCardLimit: formData.financialDebtCreditCardLimit_2,
      closeCard: formData.financialDebtCloseThisCard_2,
    },
    3: {
      type: formData.financialDebtType_3,
      company: formData.financialDebtFinanceCompany_3,
      balance: formData.financialDebtBalance_3,
      repaymentAmount: formData.financialDebtRepaymentAmount_3,
      repaymentFrequency: formData.financialDebtRepaymentFrequency_3,
      jointLoan: formData.financialDebtIsJointLoan_3,
      consolidate: formData.financialDebtConsolidate_3,
      investment: formData.financialDebtIsInvestment_3,
      creditCardLimit: formData.financialDebtCreditCardLimit_3,
      closeCard: formData.financialDebtCloseThisCard_3,
    },
    4: {
      type: formData.financialDebtType_4,
      company: formData.financialDebtFinanceCompany_4,
      balance: formData.financialDebtBalance_4,
      repaymentAmount: formData.financialDebtRepaymentAmount_4,
      repaymentFrequency: formData.financialDebtRepaymentFrequency_4,
      jointLoan: formData.financialDebtIsJointLoan_4,
      consolidate: formData.financialDebtConsolidate_4,
      investment: formData.financialDebtIsInvestment_4,
      creditCardLimit: formData.financialDebtCreditCardLimit_4,
      closeCard: formData.financialDebtCloseThisCard_4,
    },
    5: {
      type: formData.financialDebtType_5,
      company: formData.financialDebtFinanceCompany_5,
      balance: formData.financialDebtBalance_5,
      repaymentAmount: formData.financialDebtRepaymentAmount_5,
      repaymentFrequency: formData.financialDebtRepaymentFrequency_5,
      jointLoan: formData.financialDebtIsJointLoan_5,
      consolidate: formData.financialDebtConsolidate_5,
      investment: formData.financialDebtIsInvestment_5,
      creditCardLimit: formData.financialDebtCreditCardLimit_5,
      closeCard: formData.financialDebtCloseThisCard_5,
    },
    6: {
      type: formData.financialDebtType_6,
      company: formData.financialDebtFinanceCompany_6,
      balance: formData.financialDebtBalance_6,
      repaymentAmount: formData.financialDebtRepaymentAmount_6,
      repaymentFrequency: formData.financialDebtRepaymentFrequency_6,
      jointLoan: formData.financialDebtIsJointLoan_6,
      consolidate: formData.financialDebtConsolidate_6,
      investment: formData.financialDebtIsInvestment_6,
      creditCardLimit: formData.financialDebtCreditCardLimit_6,
      closeCard: formData.financialDebtCloseThisCard_6,
    },
    7: {
      type: formData.financialDebtType_7,
      company: formData.financialDebtFinanceCompany_7,
      balance: formData.financialDebtBalance_7,
      repaymentAmount: formData.financialDebtRepaymentAmount_7,
      repaymentFrequency: formData.financialDebtRepaymentFrequency_7,
      jointLoan: formData.financialDebtIsJointLoan_7,
      consolidate: formData.financialDebtConsolidate_7,
      investment: formData.financialDebtIsInvestment_7,
      creditCardLimit: formData.financialDebtCreditCardLimit_7,
      closeCard: formData.financialDebtCloseThisCard_7,
    },
    8: {
      type: formData.financialDebtType_8,
      company: formData.financialDebtFinanceCompany_8,
      balance: formData.financialDebtBalance_8,
      repaymentAmount: formData.financialDebtRepaymentAmount_8,
      repaymentFrequency: formData.financialDebtRepaymentFrequency_8,
      jointLoan: formData.financialDebtIsJointLoan_8,
      consolidate: formData.financialDebtConsolidate_8,
      investment: formData.financialDebtIsInvestment_8,
      creditCardLimit: formData.financialDebtCreditCardLimit_8,
      closeCard: formData.financialDebtCloseThisCard_8,
    },
    9: {
      type: formData.financialDebtType_9,
      company: formData.financialDebtFinanceCompany_9,
      balance: formData.financialDebtBalance_9,
      repaymentAmount: formData.financialDebtRepaymentAmount_9,
      repaymentFrequency: formData.financialDebtRepaymentFrequency_9,
      jointLoan: formData.financialDebtIsJointLoan_9,
      consolidate: formData.financialDebtConsolidate_9,
      investment: formData.financialDebtIsInvestment_9,
      creditCardLimit: formData.financialDebtCreditCardLimit_9,
      closeCard: formData.financialDebtCloseThisCard_9,
    },
    10: {
      type: formData.financialDebtType_10,
      company: formData.financialDebtFinanceCompany_10,
      balance: formData.financialDebtBalance_10,
      repaymentAmount: formData.financialDebtRepaymentAmount_10,
      repaymentFrequency: formData.financialDebtRepaymentFrequency_10,
      jointLoan: formData.financialDebtIsJointLoan_10,
      consolidate: formData.financialDebtConsolidate_10,
      investment: formData.financialDebtIsInvestment_10,
      creditCardLimit: formData.financialDebtCreditCardLimit_10,
      closeCard: formData.financialDebtCloseThisCard_10,
    },
  };
};

export const hasSectionErrorsReview = (sectionErrors: SectionErrors) => {
  return (
    Object.values(sectionErrors).filter(
      (errors) => !!errors && errors.length > 0
    ).length > 0
  );
};

export const getApplicationFormStateErrors = (
  formState: any,
  errorOnly?: boolean
) => {
  /* istanbul ignore next */
  return Object.keys(formState)
    .map((key) => [key, formState[key]])
    .filter(([, state]) => state.errors.length > 0)
    .map(([key, state]) => {
      return state.errors.map((error: any) => {
        if (errorOnly) return `${error}`;
        return `${key}: ${error}`;
      });
    })
    .flat();
};

export const getApplicationLocalErrors = (
  localErrors: any,
  errorOnly: boolean
) => {
  /* istanbul ignore next */
  return Object.keys(localErrors)
    .map((key) => [key, localErrors[key]])
    .filter(([, error]) => !!error && error.length > 0)
    .map(([key, errors]) => {
      if (Array.isArray(errors)) {
        return errors.map((error) => {
          if (errorOnly) return `${error.message}`;
          return `${key}: ${error.message}`;
        });
      } else {
        if (errorOnly) return `${errors}`;
        return `${key}: ${errors}`;
      }
    })
    .flat();
};

export const logDebugValidationsReview =
  /* istanbul ignore next */
  (formState: any, localErrors: any) => {
    if (isDebugMode()) {
      console.log("Validation results\n================");
      Object.keys(formState)
        .map((key) => [key, formState[key]])
        .filter(([, state]) => state.errors.length > 0)
        .forEach(([key, state]) => {
          state.errors.forEach((error: any) => {
            console.log(key, "||", error);
          });
        });
      Object.keys(localErrors)
        .map((key) => [key, localErrors[key]])
        .filter(([, error]) => !!error && error.length > 0)
        .forEach(([key, errors]) => {
          if (Array.isArray(errors)) {
            errors.forEach((error) => {
              console.log(key, "||", error.message);
            });
          } else {
            console.log(key, "||", errors);
          }
        });
      console.log();
      console.log("================");
    }
  };
